import {Injectable} from '@angular/core';
import {StandardSpeed} from '@domain/machine/standard-speed.enum';
import {
  AsyncGreaterThanValidator,
  ColDefBuilderFactoryService,
  CustomCellEditorComponent,
  CustomCellEditorParams,
  CustomCellEditorRendererComponent,
  CustomCellEditorRendererParams,
  LinkIconRendererComponent,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {ColDef, EditableCallbackParams, GetRowIdParams, ICellRendererParams, ITooltipParams, ValueGetterParams} from 'ag-grid-community';
import {Validation} from 'libs/angular-component-library/src/lib/grids/custom-cell-editor/validation';

@Injectable({providedIn: 'root'})
export class MatchingCriteriaOverviewService {
  public constructor(
    private readonly colDefBuilder: ColDefBuilderFactoryService,
    private readonly translate: TranslateService
  ) {}

  public getColumnDefs(showStandardEfficiencyColumn: boolean, showSpeedColumn: boolean, isEditable: boolean | ((params: EditableCallbackParams) => boolean) = true): ColDef[] {
    return [
      this.colDefBuilder
        .getBuilder()
        .withColId('type')
        .withHeaderName('GENERAL.TYPE')
        .withValueGetter((params: ValueGetterParams) => this.getResourceType(params), true)
        .withCellRenderer(LinkIconRendererComponent, (params: ICellRendererParams) => ({params, canClick: false}))
        .build(),
      this.colDefBuilder
        .getBuilder()
        .withColId('resource')
        .withHeaderName('UTILITIES.TOOL.MATCHING_CRITERIA.RESOURCE')
        .withValueGetter((params: ValueGetterParams) => this.getResource(params), true)
        .build(),
      ...(showSpeedColumn ? this.getColDefForSpeed(isEditable) : []),
      ...(showStandardEfficiencyColumn ? this.getColDefForStandardEfficiency(isEditable) : [])
    ];
  }

  public getColDefForMachineGroup(): ColDef[] {
    return [this.colDefBuilder.getBuilder().withField('id').withHide().build(), this.colDefBuilder.getBuilder().withField('name', true).withHeaderName('GENERAL.NAME').build()];
  }

  private getColDefForSpeed(isEditable: boolean | ((params: EditableCallbackParams) => boolean)): ColDef[] {
    return [
      this.colDefBuilder
        .getBuilder()
        .withHeaderName('UTILITIES.TOOL.SPEED')
        .withColIdAndField('standardSpeed')
        .withRightAlignment()
        .withEditable(
          isEditable,
          CustomCellEditorComponent,
          (params: EditableCallbackParams): CustomCellEditorParams =>
            ({
              validations: [new Validation(AsyncGreaterThanValidator.createGreaterThanValidator(0), 'greaterThan', this.translate.instant('GENERAL.ERRORS.GREATER_THAN', {greaterThan: 0}))],
              unit: this.getSpeedUnit(params),
              allowDecimals: true,
              maximumFractionDigits: 2,
              placeholder: params.data?.baseStandardSpeed?.toString() ?? '0'
            }) as CustomCellEditorParams
        )
        .withCellRenderer(
          CustomCellEditorRendererComponent,
          (params: ICellRendererParams): CustomCellEditorRendererParams =>
            ({
              placeholder: params.data?.baseStandardSpeed?.toString() ?? '0',
              unit: this.getSpeedUnit(params),
              value: params.value
            }) as CustomCellEditorRendererParams
        )
        .withTooltipValueGetter((params: ITooltipParams) => this.getSpeed(params))
        .withOnCellClicked()
        .build()
    ];
  }

  private getColDefForStandardEfficiency(isEditable: boolean | ((params: EditableCallbackParams) => boolean)): ColDef[] {
    return [
      this.colDefBuilder
        .getBuilder()
        .withHeaderName('TEXTILE_DATA.WEAVE_PRODUCT.STANDARD_EFFICIENCY', null, false, Unit.PERCENTAGE)
        .withColIdAndField('standardEfficiency')
        .withRightAlignment()
        .withEditable(
          isEditable,
          CustomCellEditorComponent,
          (params: EditableCallbackParams): CustomCellEditorParams =>
            ({
              validations: [new Validation(AsyncGreaterThanValidator.createGreaterThanValidator(0), 'greaterThan', this.translate.instant('GENERAL.ERRORS.GREATER_THAN', {greaterThan: 0}))],
              max: 100,
              unit: Unit.PERCENTAGE,
              allowDecimals: true,
              maximumFractionDigits: 2,
              placeholder: params.data?.baseStandardEfficiency?.toString() ?? '0'
            }) as CustomCellEditorParams
        )
        .withCellRenderer(
          CustomCellEditorRendererComponent,
          (params: ICellRendererParams): CustomCellEditorRendererParams =>
            ({
              placeholder: params.data?.baseStandardEfficiency?.toString() ?? '0',
              unit: Unit.PERCENTAGE,
              value: params.value
            }) as CustomCellEditorRendererParams
        )
        .withTooltipValueGetter((params: ITooltipParams) => this.getStandardEfficiency(params))
        .withOnCellClicked()
        .build()
    ];
  }

  private getStandardEfficiency(params: ICellRendererParams | ITooltipParams): string {
    return `${params.value ?? ''} %`;
  }

  private getResourceType(params: ValueGetterParams): string {
    if (params.data.machineType) {
      return this.translate.instant('MACHINE.DETAILS.MACHINE_TYPE');
    } else if (params.data.machineGroup) {
      return this.translate.instant('PLANNING.MACHINE_GROUP');
    } else {
      return this.translate.instant('MACHINE.MACHINE', {count: 1});
    }
  }

  public getResource(params: ValueGetterParams | GetRowIdParams): string {
    return params.data?.machineType?.name ?? params.data?.machine?.name ?? params.data?.machineGroup?.name;
  }

  private getSpeed(params: ICellRendererParams | ITooltipParams): string {
    return `${params.value ?? ''} ${this.getSpeedUnit(params)}`;
  }

  private getSpeedUnit(params: ICellRendererParams | ITooltipParams | EditableCallbackParams): string {
    return this.translate.instant(`UTILITIES.TOOL.SPEED_UNIT.${params.data.speedUnit ?? StandardSpeed.SPEED}`);
  }
}
